import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

/**
 * Picker chọn 1 user — search server (debounce 350ms), popover render qua PORTAL.
 * (Copy từ crm/payment-request/common — dùng chung cho Purchase Requisition + Purchase Order,
 * ví dụ: chọn Head of Dept (dropdown) trên PR, chọn Financial Controller/GM theo site ở Settings.)
 * Props:
 *   value: { id, email, name } | null
 *   onChange(user|null)
 *   fetcher(q): Promise<{ data: [{ value, email, label }] }>
 *   placeholder
 */
const initials = (s) => (String(s || "?").trim().slice(0, 2) || "?").toUpperCase();

const UserPicker = ({ value, onChange, fetcher, placeholder = "Select a person..." }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || !fetcher) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetcher(q);
        setOptions(res?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, open, fetcher]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const compute = () => {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    compute();
    const onMove = () => compute();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!btnRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => { document.removeEventListener("mousedown", h); clearTimeout(t); };
  }, [open]);

  const pick = (o) => {
    onChange({ id: o.value, email: o.email, name: (o.label || "").split(" — ")[0] });
    setOpen(false);
    setQ("");
  };

  return (
    <>
      <div
        ref={btnRef}
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        className="tw-w-full tw-h-10 tw-flex tw-items-center tw-gap-2 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-px-3 tw-text-sm tw-cursor-pointer hover:tw-border-geist-border-2 focus-within:tw-border-geist-accent tw-transition-colors"
      >
        {value?.email && (
          <span className="tw-inline-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-text-[10px] tw-font-semibold">
            {initials(value.name || value.email)}
          </span>
        )}
        <span className={`tw-flex-1 tw-min-w-0 tw-truncate ${value?.email ? "tw-text-geist-fg" : "tw-text-geist-fg-3"}`}>
          {value?.email ? `${value.name || ""} · ${value.email}` : placeholder}
        </span>
        {value?.email && (
          <i
            role="button"
            title="Clear"
            className="bi bi-x-lg tw-text-[11px] tw-text-geist-fg-3 hover:tw-text-geist-error"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          />
        )}
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[11px] tw-text-geist-fg-3 tw-shrink-0`} />
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, minWidth: 260, zIndex: 100000 }}
          className="geist-scope tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden"
        >
          <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
            <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg-2 tw-rounded-geist-sm tw-px-2.5 tw-py-1.5">
              <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name / email..."
                className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
              />
            </div>
          </div>
          <div className="tw-max-h-60 tw-overflow-y-auto tw-py-1">
            {loading ? (
              <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-geist-fg-3 tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-arrow-repeat tw-animate-spin" /> Searching...
              </div>
            ) : options.length === 0 ? (
              <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-text-center">No results.</div>
            ) : (
              options.map((o) => {
                const name = (o.label || "").split(" — ")[0];
                const selected = value?.email && value.email === o.email;
                return (
                  <div
                    key={o.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => pick(o)}
                    className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors ${selected ? "tw-bg-geist-100" : "hover:tw-bg-geist-100"}`}
                  >
                    <span className="tw-inline-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-text-[10px] tw-font-semibold">
                      {initials(name || o.email)}
                    </span>
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className="tw-block tw-text-[12px] tw-font-semibold tw-text-geist-fg tw-truncate">{name || o.email}</span>
                      <span className="tw-block tw-text-[10px] tw-text-geist-fg-3 tw-truncate">{o.email}</span>
                    </span>
                    {selected && <i className="bi bi-check-lg tw-text-geist-accent tw-text-sm tw-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

UserPicker.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    name: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  fetcher: PropTypes.func,
  placeholder: PropTypes.string,
};

export default UserPicker;
